import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnInit, Output, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { MapApi } from '../../../api/map-api';
import { UserApi } from '../../../api/user-api';
import { environment } from '../../../environment';
import { InputConverter } from '../../../inputConverter';
import { IAuthenticationService } from '../../../services/interfaces/IAuthenticationService';
import { LayerService } from '../../../services/layer.service';
import { MapService } from '../../../services/map.service';
import { ILayer, IMapCoreMap, IMapcoreSubLayer, IUserFolder, MapCoreLayer } from '../../../types';
import { BaseComponent } from '../../base.component';

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-save-map',
    templateUrl: './save-map.component.html',
    styleUrls: ['../../sharedStyles/base.scss', './save-map.component.scss']
})
export class SaveMapComponent extends BaseComponent implements OnInit, AfterViewInit {

    @Input() map: IMapCoreMap;
    @Input() mapId;

    windowRef;
    @Input() mapRef: NonNullable<__esri.Map>;
    @Input() mapViewRef: NonNullable<__esri.MapView>;
    @Input() userId: number = null;
    @Input() applicationId: number = environment.appId;
    @Input()
    @InputConverter()
    showPublic: boolean = true;
    isNewMap?: boolean = null;
    isSaving: boolean = false;
    isSystem = false;
    createNewFolder = false;
    newFolder: IUserFolder = null;
    parentFolders: IUserFolder[] = [];
    parentFolderId: number = 0;
    mapLayers: ILayer[] = [];
    unsavedLayers = [];
    disableCreateMap: boolean = true;
    isExistingMap: boolean = false;
    nonPublicLayers = [];
    isLoading: boolean = false;

    @Output() public readonly onsave = new EventEmitter<string>();

    PUBLIC_LAYER_NAMES = new Set([
        'View Public Data',
        'MapCore Public Layers',
        'Mapcore Public Layers'
    ]);

    constructor(
        _vcr: ViewContainerRef,
        @Inject('authenticationService') private readonly authService: IAuthenticationService,
        private readonly layerService: LayerService,
        private readonly mapService: MapService,
        private readonly userApi: UserApi,
        private readonly mapApi: MapApi,
        private readonly cdr: ChangeDetectorRef
    ) {
        super(_vcr);
        this.registerEvent('onsave', this.onsave);
        this.getClosestAncestor('mc-window').then((window: any) => {
            if (window) {
                this.windowRef = window;
                this.windowRef.addEventListener('openChange', open => {
                    if (open.detail) {
                        this.windowOpened();
                    }
                });
            } else {
                this.windowOpened();
            }
        });
    }

    ngOnInit() { }

    ngAfterViewInit() {
        this.isReady();
    }

    async windowOpened() {
        this.isLoading = true;
        this.createNewFolder = false;
        this.newFolder = null;
        this.isNewMap = true; //default to true
        this.isSaving = false;
        this.disableCreateMap = true;
        this.parentFolderId = 0;
        this.nonPublicLayers = [];
        if (!this.mapRef) {
            const map = await this.getClosestAncestor('mc-map');
            if (map) {
                await map.ready;
                this.mapRef = map.map;
                this.mapViewRef = map.mapView;
            } else {
                this.readyProblem('Map View Reference not supplied and reference from parent element could not be retrieved.');
            }
        }

        if (this.userId === null) {
            await this.authService.GetCurrentUser().subscribe(user => {
                if (user) {
                    this.userId = user.id;
                }
            });
        }

        this.map = (<any>this.mapViewRef).mapcoreViewProps;
        if (!this.map) {
            this.map = this.createNewMap();
        }

        if (this.isNewMap === null) {
            this.isNewMap = this.map.isSystem;
        }

        this.parentFolders = await this.userApi.queryUserFolders(this.userId, this.applicationId, 'maps');
        this.parentFolders = this.parentFolders.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
        this.parentFolders.unshift({ id: 0, name: 'No parent' });

        if (this.map.id !== null) {
            const data = await this.mapService.getUserMap(this.userId, this.map.id, environment.appId);
            const userMap = data && data.length && data[0].userMaps && data[0].userMaps.length ? data[0].userMaps[0].map : null;
            this.isExistingMap = !!userMap;
            if (this.isExistingMap) {
                //if this is an existing map, check if it the user owns it, if so prevent forcing 'create new map'
                if (userMap && userMap.userMaps.filter(m => m.userId === this.userId && m.accessLevel === 'OWNER').length) {
                    this.disableCreateMap = false;
                    this.isNewMap = false;
                }

                //if this is an existing map, update the selected parent folder if needed
                const userMapFolders = (await this.mapService.getUserMapsFolders(this.userId, environment.appId))[0].folders;

                let existingMapsFolderId = -1;
                for (const folder of userMapFolders) {
                    for (const map of folder.maps) {
                        if (map.id === this.map.id) {
                            existingMapsFolderId = folder.id;
                            break;
                        }
                    }
                    if (existingMapsFolderId !== -1) { break; }
                }
                this.parentFolderId = existingMapsFolderId !== -1 ? existingMapsFolderId : 0;
            }
            this.cdr.detectChanges();
        }

        this.isLoading = false;
    }

    createNewMap() {
        return {
            applicationID: this.applicationId,
            description: '',
            id: null,
            isPublic: false,
            isSystem: false,
            name: '',
            sharedWith: [],
            userId: this.userId
        };
    }

    isNewFolder(event) {
        this.createNewFolder = event;
        if (this.createNewFolder) {
            this.newFolder = {
                name: '',
                category: 'maps',
                parentUserFolderId: this.parentFolderId,
                userId: this.userId,
                applicationId: this.applicationId
            };
        } else {
            this.newFolder = null;
        }
    }

    parentFolderSelected(value: number) {
        if (this.createNewFolder) {
            this.newFolder.parentUserFolderId = value;
        } else {
            this.parentFolderId = value;
        }
    }

    @Input()
    public saveMap = async () => {
        this.isSaving = true;
        let layers = (<any>this.mapRef.layers).items as MapCoreLayer[];
        layers = layers.filter(x => x.mapcoreProps && !x.mapcoreProps.hideInLegend && (!x.listMode || x.listMode !== 'hide'));

        if (this.hasNonPublicLayers(layers)) {
            return;
        }

        this.mapLayers = [];
        const originalLayers = [];

        if (this.mapRef.basemap) {
            // 2/16/24, Onemap uses terrain basemap with id "Terrain" (which will hit else below) and MIQ switched to Esri terrain basemap with id "terrain"
            const basemapIds = ['terrain', 'streets-vector', 'satellite', 'hybrid', 'topo-vector', 'gray-vector', 'osm'];
            if (basemapIds.includes(this.mapRef.basemap.id)) {
                this.map.basemap = JSON.stringify({ type: 'ID', id: `${this.mapRef.basemap.id}` });
            } else if (this.mapRef.basemap.id.includes('Bing')) {
                this.map.basemap = JSON.stringify({ type: 'BING', id: `${this.mapRef.basemap.id}` });
            } else {
                this.map.basemap = JSON.stringify({ type: 'CONFIG', id: `${this.mapRef.basemap.id}` });
            }
        }

        for (const layer of layers) {
            if (layer.type !== 'group') {
                originalLayers.push(layer);
                const mapcoreLayer = this.layerService.getLayerForSaveMap(layer, this.isNewMap);
                this.mapLayers.push(mapcoreLayer);
            } else {
                const mapcoreLayer = this.layerService.getLayerForSaveMap(layer, this.isNewMap);
                this.mapLayers.push(mapcoreLayer);

                for (const groupedLayer of layer.layers.toArray()) {
                    originalLayers.push(groupedLayer);
                    const groupedMapcoreLayer = this.layerService.getLayerForSaveMap(groupedLayer, this.isNewMap);
                    this.mapLayers.push(groupedMapcoreLayer);
                }
            }
        }

        if (this.map.isSystem || this.isNewMap) {
            this.map.id = null;
            this.map.isSystem = false;
            this.map.layers = null;
        }
        this.map.extentJSON = JSON.stringify(this.mapViewRef.extent.toJSON());

        this.map = await this.mapApi.saveMap(this.map, this.mapLayers, environment.appId);
        // save map returns layers unordered, change to saved order
        this.map.layers = this.mapLayers
            .filter(mapLayer => !mapLayer.isGroupParent)
            .map(mapLayer => {
                const layerInOrder = this.map.layers.find(layer => layer.name === mapLayer.name);
                return layerInOrder as IMapcoreSubLayer;
            });
        (<any>this.mapViewRef).mapcoreViewProps = this.map;

        if (this.createNewFolder) {
            const currentFolder = await this.userApi.createNewUserFolder(this.newFolder);
            if (currentFolder) {
                this.map.parentFolderId = currentFolder.id;
            }
        }

        if (!this.map.parentFolderId) {
            this.map.parentFolderId = this.parentFolderId;
        }

        if (this.map.parentFolderId) {
            await this.mapApi.saveMapToUserFolder(this.map.id, this.map.parentFolderId);
        }

        if (this.map.layers && this.map.layers.length > 0) {
            const layerIds = this.map.layers.map(l => l.id);
            const layersFromApi = await this.layerService.getLayersFromApi(layerIds);

            this.map.layers.forEach((layer, index) => {
                const layerRef: MapCoreLayer = originalLayers[index];
                if (layerRef && layer && layer.id) {
                    layerRef.mapcoreProps.id = layer.id;
                    layerRef.mapcoreProps.name = layer.name;

                    const savedLayer = layersFromApi.filter(l => l.id === layer.id)[0];
                    layerRef.mapcoreProps.idFromSavedLayer = savedLayer.id;
                    layerRef.mapcoreProps.isSystem = savedLayer.isSystem;
                    layerRef.mapcoreProps.parentId = savedLayer.parentId;
                    layerRef.mapcoreProps.typeId = savedLayer.typeId;

                    // Only populated when adding layer to map; once map is saved this should be cleared since it wouldn't be populated if layer is already in map
                    layerRef.mapcoreProps.idFromSavedMap = null;
                }
            });
        }

        this.onsave.emit(this.map.id.toString());
        this.isSaving = false;
        this.closeWindow();
    }

    closeWindow() {
        this.isNewMap = null;
        this.windowRef.close();
    }

    private hasNonPublicLayers(layers: any[]): boolean {
        this.nonPublicLayers = [];

        if (!this.map.isPublic) {
            return false;
        }

        for (const layer of layers) {
            if (layer.mapcoreProps.url) {
                // Feature layers don't have permissions
                continue;
            }

            if (layer.mapcoreProps.permissions
                && !layer.mapcoreProps.permissions.some(p => this.PUBLIC_LAYER_NAMES.has(p.name))) {
                this.nonPublicLayers.push(layer.mapcoreProps);
            }
        }

        return this.nonPublicLayers.length > 0;
    }

    folderTrack(_index: number, item: IUserFolder) {
        return item.id;
    }
}
